import {
  cellDust,
  drainAmounts,
  dustKey,
  DustMap,
  dustTotal,
  inBounds,
  MutableSpeciesAmounts,
  SpeciesAmounts,
} from "../Dust";
import { CleaningPass, CleaningWorld } from "./dust-actions";
import {
  canisterRoom,
  carryingShopVac,
  SHOP_VAC_EMPTY_RATE,
  SHOP_VAC_PASSIVE_RATE,
} from "../ShopVac";
import { personCanWork } from "../Person";
import { DustSpecies } from "../Materials";
import { nextToGarbageCan, sweepSwath } from "./dust-actions";
import { vectorEquals } from "../Vectors";
import { withXp } from "../skill-helpers";
import { shareOverMinutes } from "../time";

/**
 * The shop vac's work: the hose's suction, and the passive trickle a
 * running vac pulls in on its own. Both are pure passes over a shop
 * snapshot for a span of shop minutes; the CleaningSystem runs them
 * every engine tick and writes the slices they own back onto its
 * singletons.
 */

/** Share of the underfoot cell's dust a minute of suction takes. */
const VACUUM_UNDERFOOT_RATE = 0.9;
/** The facing cone pulls slower — but machine undersides included: the
 * hose reaches where the broom fumbles. */
const VACUUM_CONE_RATE = 0.45;
/** Suction gathering less than this per minute grants no XP (mirrors
 * sweeping). */
const XP_MINIMUM_RATE = 15;
/** Craft XP per minute of heavy suction. */
const VACUUM_XP_PER_MINUTE = 1;

/**
 * `minutes` of suction, run while the player holds the operate key with
 * the hose in hand — the same held-Space idiom as the
 * broom, with the vac's own shape: the same swath of cells, machine
 * undersides very much included, cleaned to zero instead of pushed into
 * a pile. Dust goes into the canister until it's full; a full canister
 * kills the suction until it's emptied.
 *
 * Standing next to the garbage can, the same hold empties the canister
 * instead, steadily — the trip is the chore, and the emptying is
 * deliberately a moment rather than a silent side effect.
 */
export function vacuumPass(minutes: number): CleaningPass {
  return (gameState) => {
    const vac = gameState.shopVac;
    if (
      !vac ||
      !carryingShopVac(gameState) ||
      gameState.player.operating !== true ||
      !personCanWork(gameState.player)
    ) {
      return gameState;
    }

    // At the can, the hold drains the canister
    if (
      dustTotal(vac.canister) > 0 &&
      nextToGarbageCan(gameState, gameState.player.position)
    ) {
      return {
        ...gameState,
        shopVac: {
          ...vac,
          canister: drainAmounts(vac.canister, SHOP_VAC_EMPTY_RATE * minutes),
        },
      };
    }

    const room = canisterRoom(vac);
    if (room <= 0) {
      return gameState;
    }

    const shopSize = gameState.shopInfo.size;
    const swath = sweepSwath(
      gameState.player.position,
      gameState.player.direction,
    ).filter((cell) => inBounds(cell, shopSize));

    const gathered: Array<{ key: string; amounts: SpeciesAmounts }> = [];
    for (const cell of swath) {
      const amounts = gameState.dust[dustKey(cell)];
      if (!amounts) {
        continue;
      }
      const rate = shareOverMinutes(
        vectorEquals(cell, gameState.player.position)
          ? VACUUM_UNDERFOOT_RATE
          : VACUUM_CONE_RATE,
        minutes,
      );
      const taken: MutableSpeciesAmounts = {};
      for (const [species, amount] of Object.entries(amounts)) {
        if ((amount ?? 0) > 0) {
          taken[species as DustSpecies] = (amount ?? 0) * rate;
        }
      }
      if (dustTotal(taken) > 0) {
        gathered.push({ key: dustKey(cell), amounts: taken });
      }
    }

    const gatheredTotal = gathered.reduce(
      (sum, { amounts }) => sum + dustTotal(amounts),
      0,
    );
    if (gatheredTotal <= 0) {
      return gameState;
    }
    const keepFraction = Math.min(1, room / gatheredTotal);

    const { dust, moved } = moveDustToCanister(
      gameState.dust,
      vac.canister,
      gathered,
      keepFraction,
    );

    const vacuumed = {
      ...gameState,
      dust,
      shopVac: { ...vac, canister: moved },
    };
    const gainedXp =
      (gatheredTotal * keepFraction) / minutes >= XP_MINIMUM_RATE;
    return gainedXp
      ? withXp(vacuumed, VACUUM_XP_PER_MINUTE * minutes)
      : vacuumed;
  };
}

/**
 * The vac's behavior while it's being dragged: a passive trickle-clean
 * of the cell underfoot — the nozzle drags over whatever you walk
 * across. Emptying is not here: that's a deliberate hold at the garbage
 * can (vacuumPass), not a side effect of walking past it.
 */
export function shopVacPass(minutes: number): CleaningPass {
  return (gameState) => {
    const vac = gameState.shopVac;
    if (!vac || !carryingShopVac(gameState) || gameState.player.away) {
      return gameState;
    }

    const underfoot = gameState.dust[dustKey(gameState.player.position)];
    const room = canisterRoom(vac);
    if (!underfoot || room <= 0) {
      return gameState;
    }
    const total = dustTotal(underfoot);
    const take = Math.min(SHOP_VAC_PASSIVE_RATE * minutes, total, room);
    if (take <= 0) {
      return gameState;
    }
    const { dust, moved } = moveDustToCanister(
      gameState.dust,
      vac.canister,
      [{ key: dustKey(gameState.player.position), amounts: underfoot }],
      take / total,
    );
    return { ...gameState, dust, shopVac: { ...vac, canister: moved } };
  };
}

/**
 * Move `keepFraction` of the gathered amounts from the floor into the
 * canister, dropping floor cells that come up clean. Shared by the
 * suction pass and the passive trickle.
 */
function moveDustToCanister(
  dustMap: DustMap,
  canister: SpeciesAmounts,
  gathered: ReadonlyArray<{ key: string; amounts: SpeciesAmounts }>,
  keepFraction: number,
): { dust: DustMap; moved: SpeciesAmounts } {
  const CLEAN_EPSILON = 0.05;
  const dust: Record<string, MutableSpeciesAmounts> = {
    ...dustMap,
  };
  const contents: MutableSpeciesAmounts = { ...canister };
  for (const { key, amounts } of gathered) {
    const cell: MutableSpeciesAmounts = { ...dust[key] };
    for (const [species, amount] of Object.entries(amounts)) {
      const moved = (amount ?? 0) * keepFraction;
      contents[species as DustSpecies] =
        (contents[species as DustSpecies] ?? 0) + moved;
      const left = (cell[species as DustSpecies] ?? 0) - moved;
      if (left > CLEAN_EPSILON) {
        cell[species as DustSpecies] = left;
      } else {
        delete cell[species as DustSpecies];
      }
    }
    if (dustTotal(cell)) {
      dust[key] = cell;
    } else {
      delete dust[key];
    }
  }
  return { dust, moved: contents };
}

/** Anything suction from here could pick up? Drives the hint. */
export function canVacuumAt(gameState: CleaningWorld): boolean {
  return sweepSwath(gameState.player.position, gameState.player.direction).some(
    (cell) => cellDust(gameState.dust, cell) > 0.05,
  );
}
